import React, { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';

function formatDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  return date.toLocaleString();
}

function formatValue(value) {
  const raw = String(value ?? '').trim();
  return raw || '-';
}

function formatActor(actor) {
  if (!actor || typeof actor !== 'object') {
    return '-';
  }

  return formatValue(actor.email || actor.username || actor.name || actor.uid || actor.id);
}

function formatHistoryValue(entry, type) {
  const bankValue = formatValue(entry?.[`${type}RateBank`]);
  const userValue = formatValue(entry?.[`${type}RateUser`]);

  if (bankValue === '-' && userValue === '-') {
    return '-';
  }

  if (bankValue === '-') {
    return userValue;
  }

  if (userValue === '-') {
    return bankValue;
  }

  return `${bankValue} / ${userValue}`;
}

const RELOAD_STATUS_LABEL_KEYS = {
  never: 'currencyRates.reloadStatusNever',
  success: 'currencyRates.reloadStatusSuccess',
  failure: 'currencyRates.reloadStatusFailure',
};

const HISTORY_ACTION_LABEL_KEYS = {
  ecb_reload: 'currencyRates.historyActionEcbReload',
  manual_created: 'currencyRates.historyActionManualCreated',
  override_changed: 'currencyRates.historyActionOverrideChanged',
  override_reset: 'currencyRates.historyActionOverrideReset',
};

function translateReloadStatus(t, status) {
  const key = RELOAD_STATUS_LABEL_KEYS[String(status || 'never').trim().toLowerCase()];
  return t(key || 'currencyRates.reloadStatusUnknown');
}

function translateHistoryAction(t, action) {
  const key = HISTORY_ACTION_LABEL_KEYS[String(action || '').trim().toLowerCase()];
  return t(key || 'currencyRates.historyActionUnknown');
}

function translateRowStatus(t, row) {
  return row?.isOverridden ? t('currencyRates.statusManual') : t('currencyRates.statusEcb');
}

function translateFormError(t, field, message) {
  if ((field === 'currencyBase' || field === 'currency') && message === 'Currency code must use 3 letters.') {
    return t('currencyRates.currencyInvalid');
  }

  if (field === 'currencyBase' && message === 'Base currency is required.') {
    return t('currencyRates.baseCurrencyRequired');
  }

  if (field === 'currency' && message === 'Currency is required.') {
    return t('currencyRates.currencyRequired');
  }

  if (field === 'currency' && message === 'Currency pair must use different currencies.') {
    return t('currencyRates.currencyPairInvalid');
  }

  if (field === 'reason' && message === 'Reason is required.') {
    return t('currencyRates.reasonRequired');
  }

  if (field === 'rate' && message === 'Rate must use decimal format.') {
    return t('currencyRates.rateInvalid');
  }

  return message;
}

function ModalFrame({ title, children, footer, onClose }) {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const generatedId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const titleId = `currency-rates-modal-title-${generatedId || 'dialog'}`;
  const { t } = useTranslation();

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const focusable = dialogRef.current?.querySelector(
      [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', '),
    );

    if (focusable && typeof focusable.focus === 'function') {
      focusable.focus();
    } else if (dialogRef.current) {
      dialogRef.current.focus();
    }

    return () => {
      const previousFocus = previousFocusRef.current;
      if (previousFocus && document.contains(previousFocus) && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <>
      <div
        ref={dialogRef}
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 id={titleId} className="modal-title">{title}</h5>
              <button type="button" className="btn-close" aria-label={t('common.close')} onClick={onClose}></button>
            </div>
            {children}
            {footer}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

function CurrencyRatesPageView({
  rows = [],
  visibleRows = [],
  filters = { text: '', status: 'all' },
  reloadStatus = {},
  isLoading = false,
  isSaving = false,
  isHistoryLoading = false,
  error = '',
  success = '',
  modalError = '',
  formErrors = {},
  form = { rate: '', reason: '' },
  isCreateOpen = false,
  editTarget = null,
  resetTarget = null,
  historyTarget = null,
  historyRows = [],
  historyTotal = 0,
  canManageCurrencyRates = false,
  updateFilter = () => {},
  resetFilters = () => {},
  refresh = () => {},
  reloadRates = () => {},
  openCreate = () => {},
  openEdit = () => {},
  openReset = () => {},
  openHistory = () => {},
  closeModals = () => {},
  onFormChange = () => {},
  createRate = () => {},
  saveOverride = () => {},
  confirmReset = () => {},
}) {
  const { t } = useTranslation();
  const shownRows = Array.isArray(visibleRows) ? visibleRows : [];
  const loadedCount = Number(reloadStatus?.loadedCount ?? 0);

  const handleEditSubmit = (event) => {
    event.preventDefault();
    saveOverride(event);
  };

  const handleCreateSubmit = (event) => {
    event.preventDefault();
    createRate(event);
  };

  const handleResetSubmit = (event) => {
    event.preventDefault();
    confirmReset(event);
  };

  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('currencyRates.savingInfo') : ''}
        error={error}
        success={success}
      />

      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <PageHeader title={t('currencyRates.title')} subtitle={t('currencyRates.subtitle')} />
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-default"
            title={t('currencyRates.refresh')}
            onClick={refresh}
            disabled={isLoading || isSaving}
          >
            <i className="ti ti-refresh me-1"></i>
            {t('currencyRates.refresh')}
          </button>
          {canManageCurrencyRates ? (
            <>
              <button
                type="button"
                className="btn btn-default"
                title={t('currencyRates.addRate')}
                onClick={openCreate}
                disabled={isLoading || isSaving}
              >
                <i className="ti ti-plus me-1"></i>
                {t('currencyRates.addRate')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                title={t('currencyRates.reloadFromEcb')}
                onClick={reloadRates}
                disabled={isLoading || isSaving}
              >
                <i className="ti ti-refresh me-1"></i>
                {t('currencyRates.reloadFromEcb')}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="card card-body mb-3">
        <div className="row g-3">
          <div className="col-md-4">
            <div className="text-muted small">{t('currencyRates.lastReload')}</div>
            <div className="fw-semibold">{translateReloadStatus(t, reloadStatus?.status)}</div>
            <div className="text-muted small">{formatDateTime(reloadStatus?.completedAt || reloadStatus?.startedAt)}</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted small">{t('currencyRates.loadedCount')}</div>
            <div className="fw-semibold">{loadedCount}</div>
            <div className="text-muted small">{shownRows.length} / {rows.length}</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted small">{t('currencyRates.lastError')}</div>
            <div className="fw-semibold text-break">{formatValue(reloadStatus?.errorMessage)}</div>
          </div>
        </div>
      </div>

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="currency-rates-search">{t('currencyRates.search')}</label>
              <input
                id="currency-rates-search"
                className="form-control"
                value={filters.text || ''}
                onChange={(event) => updateFilter('text', event.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="currency-rates-status">{t('currencyRates.status')}</label>
              <select
                id="currency-rates-status"
                className="form-select"
                value={filters.status || 'all'}
                onChange={(event) => updateFilter('status', event.target.value)}
              >
                <option value="all">{t('currencyRates.statusAll')}</option>
                <option value="bank">{t('currencyRates.statusEcb')}</option>
                <option value="manual">{t('currencyRates.statusManual')}</option>
              </select>
            </div>
            <div className="col-md-2 d-grid">
              <button type="button" className="btn btn-default" onClick={resetFilters} disabled={isLoading || isSaving}>
                {t('common.reset')}
              </button>
            </div>
          </div>

          <LoadingContainer isLoading={isLoading} hasData={shownRows.length > 0}>
            <table className="table search-table align-middle mb-0">
              <thead className="header-item">
                <tr>
                  <th>{t('currencyRates.colPair')}</th>
                  <th>{t('currencyRates.colBankRate')}</th>
                  <th>{t('currencyRates.colUserRate')}</th>
                  <th>{t('currencyRates.colEffectiveRate')}</th>
                  <th>{t('currencyRates.colStatus')}</th>
                  <th>{t('currencyRates.colModified')}</th>
                  <th>{t('currencyRates.colLastChangedBy')}</th>
                  <th className="text-end" style={{ width: '1%', whiteSpace: 'nowrap' }}>{t('currencyRates.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">{t('currencyRates.noRates')}</td>
                  </tr>
                ) : null}

                {shownRows.map((row) => (
                  <tr key={row.key || row.id || row.pairLabel}>
                    <td className="fw-semibold text-nowrap">{formatValue(row.pairLabel)}</td>
                    <td className="text-nowrap">{formatValue(row.rateBank)}</td>
                    <td className="text-nowrap">{formatValue(row.isOverridden ? row.rateUser : '')}</td>
                    <td className="text-nowrap">{formatValue(row.effectiveRate)}</td>
                    <td>
                      <span className={`badge ${row.isOverridden ? 'bg-warning-subtle text-warning' : 'bg-info-subtle text-info'}`}>
                        {translateRowStatus(t, row)}
                      </span>
                    </td>
                    <td className="text-nowrap">{row.modified || row.lastChangedAt ? formatDateTime(row.lastChangedAt) : '-'}</td>
                    <td className="text-break">{formatActor(row.lastChangedBy)}</td>
                    <td className="text-end text-nowrap">
                      <div className="d-flex justify-content-end gap-2">
                        {canManageCurrencyRates ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              title={t('currencyRates.editOverride')}
                              onClick={() => openEdit(row)}
                              disabled={isSaving}
                            >
                              <i className="fa fa-pen"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-default"
                              title={t('currencyRates.resetOverride')}
                              onClick={() => openReset(row)}
                              disabled={isSaving || !row.isOverridden || !row.rateBank}
                            >
                              <i className="ti ti-rotate-clockwise"></i>
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-sm btn-default"
                          title={t('currencyRates.history')}
                          onClick={() => openHistory(row)}
                          disabled={isSaving}
                        >
                          <i className="ti ti-history"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </LoadingContainer>
        </div>
      </div>

      {isCreateOpen ? (
        <ModalFrame title={t('currencyRates.addRate')} onClose={closeModals}>
          <form data-testid="currency-rate-create-form" onSubmit={handleCreateSubmit} noValidate>
            <div className="modal-body">
              {modalError ? <div className="alert alert-danger py-2">{modalError}</div> : null}
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label" htmlFor="currency-rate-create-base">{t('currencyRates.baseCurrency')} *</label>
                  <input
                    id="currency-rate-create-base"
                    className={`form-control text-uppercase ${formErrors.currencyBase ? 'is-invalid' : ''}`}
                    value={form.currencyBase || ''}
                    maxLength="3"
                    onChange={(event) => onFormChange('currencyBase', event.target.value.toUpperCase())}
                    aria-invalid={formErrors.currencyBase ? 'true' : 'false'}
                    required
                  />
                  {formErrors.currencyBase ? <div className="invalid-feedback">{translateFormError(t, 'currencyBase', formErrors.currencyBase)}</div> : null}
                </div>
                <div className="col-md-6">
                  <label className="form-label" htmlFor="currency-rate-create-currency">{t('currencyRates.currency')} *</label>
                  <input
                    id="currency-rate-create-currency"
                    className={`form-control text-uppercase ${formErrors.currency ? 'is-invalid' : ''}`}
                    value={form.currency || ''}
                    maxLength="3"
                    onChange={(event) => onFormChange('currency', event.target.value.toUpperCase())}
                    aria-invalid={formErrors.currency ? 'true' : 'false'}
                    required
                  />
                  {formErrors.currency ? <div className="invalid-feedback">{translateFormError(t, 'currency', formErrors.currency)}</div> : null}
                </div>
                <div className="col-12">
                  <label className="form-label" htmlFor="currency-rate-create-rate">{t('currencyRates.rate')} *</label>
                  <input
                    id="currency-rate-create-rate"
                    className={`form-control ${formErrors.rate ? 'is-invalid' : ''}`}
                    value={form.rate || ''}
                    onChange={(event) => onFormChange('rate', event.target.value)}
                    aria-invalid={formErrors.rate ? 'true' : 'false'}
                    required
                  />
                  {formErrors.rate ? <div className="invalid-feedback">{translateFormError(t, 'rate', formErrors.rate)}</div> : null}
                </div>
                <div className="col-12">
                  <label className="form-label" htmlFor="currency-rate-create-reason">{t('currencyRates.reason')} *</label>
                  <textarea
                    id="currency-rate-create-reason"
                    className={`form-control ${formErrors.reason ? 'is-invalid' : ''}`}
                    rows="3"
                    value={form.reason || ''}
                    onChange={(event) => onFormChange('reason', event.target.value)}
                    aria-invalid={formErrors.reason ? 'true' : 'false'}
                    required
                  ></textarea>
                  {formErrors.reason ? <div className="invalid-feedback">{translateFormError(t, 'reason', formErrors.reason)}</div> : null}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={closeModals}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={isSaving}>{t('common.save')}</button>
            </div>
          </form>
        </ModalFrame>
      ) : null}

      {editTarget ? (
        <ModalFrame title={`${t('currencyRates.editOverride')} ${formatValue(editTarget.pairLabel)}`} onClose={closeModals}>
          <form data-testid="currency-rate-edit-form" onSubmit={handleEditSubmit} noValidate>
            <div className="modal-body">
              {modalError ? <div className="alert alert-danger py-2">{modalError}</div> : null}
              <div className="mb-3">
                <label className="form-label" htmlFor="currency-rate-override-rate">{t('currencyRates.rate')} *</label>
                <input
                  id="currency-rate-override-rate"
                  className={`form-control ${formErrors.rate ? 'is-invalid' : ''}`}
                  value={form.rate || ''}
                  onChange={(event) => onFormChange('rate', event.target.value)}
                  aria-invalid={formErrors.rate ? 'true' : 'false'}
                  required
                />
                {formErrors.rate ? <div className="invalid-feedback">{translateFormError(t, 'rate', formErrors.rate)}</div> : null}
              </div>
              <div className="mb-0">
                <label className="form-label" htmlFor="currency-rate-override-reason">{t('currencyRates.reason')} *</label>
                <textarea
                  id="currency-rate-override-reason"
                  className={`form-control ${formErrors.reason ? 'is-invalid' : ''}`}
                  rows="3"
                  value={form.reason || ''}
                  onChange={(event) => onFormChange('reason', event.target.value)}
                  aria-invalid={formErrors.reason ? 'true' : 'false'}
                  required
                ></textarea>
                {formErrors.reason ? <div className="invalid-feedback">{translateFormError(t, 'reason', formErrors.reason)}</div> : null}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={closeModals}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={isSaving}>{t('common.save')}</button>
            </div>
          </form>
        </ModalFrame>
      ) : null}

      {resetTarget ? (
        <ModalFrame title={`${t('currencyRates.resetOverride')} ${formatValue(resetTarget.pairLabel)}`} onClose={closeModals}>
          <form data-testid="currency-rate-reset-form" onSubmit={handleResetSubmit} noValidate>
            <div className="modal-body">
              {modalError ? <div className="alert alert-danger py-2">{modalError}</div> : null}
              <div className="mb-0">
                <label className="form-label" htmlFor="currency-rate-reset-reason">{t('currencyRates.reason')} *</label>
                <textarea
                  id="currency-rate-reset-reason"
                  className={`form-control ${formErrors.reason ? 'is-invalid' : ''}`}
                  rows="3"
                  value={form.reason || ''}
                  onChange={(event) => onFormChange('reason', event.target.value)}
                  aria-invalid={formErrors.reason ? 'true' : 'false'}
                  required
                ></textarea>
                {formErrors.reason ? <div className="invalid-feedback">{translateFormError(t, 'reason', formErrors.reason)}</div> : null}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={closeModals}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-danger" disabled={isSaving}>{t('common.confirm')}</button>
            </div>
          </form>
        </ModalFrame>
      ) : null}

      {historyTarget ? (
        <ModalFrame title={`${t('currencyRates.history')} ${formatValue(historyTarget.pairLabel)}`} onClose={closeModals}>
          <div className="modal-body">
            {modalError ? <div className="alert alert-danger py-2">{modalError}</div> : null}
            <div className="text-muted small mb-2">{t('currencyRates.historyTotal')}: {historyTotal}</div>
            <LoadingContainer isLoading={isHistoryLoading} hasData={historyRows.length > 0}>
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>{t('currencyRates.colAction')}</th>
                    <th>{t('currencyRates.colOldValue')}</th>
                    <th>{t('currencyRates.colNewValue')}</th>
                    <th>{t('currencyRates.colReason')}</th>
                    <th>{t('currencyRates.colActor')}</th>
                    <th>{t('currencyRates.colTimestamp')}</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">{t('currencyRates.noHistory')}</td>
                    </tr>
                  ) : null}
                  {historyRows.map((entry, index) => (
                    <tr key={`${entry.createdAt || index}-${entry.action || 'history'}`}>
                      <td className="text-nowrap">{translateHistoryAction(t, entry.action)}</td>
                      <td className="text-nowrap">{formatHistoryValue(entry, 'old')}</td>
                      <td className="text-nowrap">{formatHistoryValue(entry, 'new')}</td>
                      <td className="text-break">{formatValue(entry.reason)}</td>
                      <td className="text-break">{formatActor(entry.actor)}</td>
                      <td className="text-nowrap">{formatDateTime(entry.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </LoadingContainer>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-default" onClick={closeModals}>{t('common.close')}</button>
          </div>
        </ModalFrame>
      ) : null}
    </div>
  );
}

export default CurrencyRatesPageView;
